import { useCallback, useEffect, useRef, useState } from 'react';
import { festAPI } from '@/lib/api';

/* Sommerfest-Scanner — Mitarbeiter, hinter Login (Backend prüft fest.aktionen).
 *
 * Kamera liest den Gast-QR (jsQR, gebündelt — die CSP erlaubt kein CDN),
 * alternativ Losnummer eintippen. Danach vier Aktionsblöcke:
 * Check-in · Challenge abhaken · Probetraining buchen · Gymfights-Ticket.
 *
 * EINGANGS-MODUS: Am Einlass zählt Tempo, nicht Auswahl. Ist er an, bleibt
 * die Kamera dauerhaft offen und jeder erkannte QR checkt sofort ein —
 * Karte hoch, grüner Balken, nächster. Kein Antippen dazwischen. Dieselbe
 * Karte wird 6 Sekunden lang ignoriert, damit eine Karte, die noch im Bild
 * hängt, nicht mehrfach feuert (das Backend ist zusätzlich idempotent).
 *
 * Optimiert für Daumen, Hochformat, Sonne: große Ziele, starker Kontrast.
 * Offline-Verhalten: schlägt eine Aktion fehl, bleibt sie als "ausstehend"
 * in einer Warteschlange und wird beim nächsten Versuch mitgesendet.
 */

const QUEUE_KEY = 'fest_scan_queue';

function queueLaden() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { return []; }
}
function queueSpeichern(q) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch { /* egal */ }
}

/** Uhrzeit in Ortszeit — die Zeitstempel kommen als UTC-ISO vom Server. */
function uhrzeit(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? ''
    : d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

/* Der Balken am Einlass. Aus zwei Metern lesbar, Farbe sagt alles:
   grün durch, gelb war schon da, rot stimmt was nicht. */
function CheckinBalken({ e }) {
  if (e.unbekannt) {
    return <div className="fs-balken fs-balken-rot" data-testid="balken">
      <b>Karte unbekannt</b><span>Bitte am Stand neu anlegen</span></div>;
  }
  if (e.ausstehend) {
    return <div className="fs-balken fs-balken-gelb" data-testid="balken">
      <b>Kein Netz — gemerkt</b><span>Wird automatisch nachgetragen. Gast durchlassen.</span></div>;
  }
  if (e.bereits) {
    return <div className="fs-balken fs-balken-gelb" data-testid="balken">
      <b>{e.vorname} war schon da</b>
      <span>Eingecheckt um {uhrzeit(e.zeit)}{e.band ? ` · Band ${e.band}` : ''}</span></div>;
  }
  return <div className="fs-balken fs-balken-gruen" data-testid="balken">
    <b>Willkommen, {e.vorname}!</b>
    <span>#{e.losnummer}{e.band ? ` · Band ${e.band}` : ''}</span></div>;
}

export default function FestScanPage() {
  const [karte, setKarte] = useState(null);
  const [status, setStatus] = useState('');       // kurzes Feedback
  const [fehler, setFehler] = useState('');
  const [losEingabe, setLosEingabe] = useState('');
  const [kameraAn, setKameraAn] = useState(false);
  const [queue, setQueue] = useState(queueLaden);
  const [busy, setBusy] = useState(false);
  const [eingangsModus, setEingangsModus] = useState(false);
  const [letzterCheckin, setLetzterCheckin] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  // Refs, damit die Kamera-Schleife den aktuellen Stand sieht, ohne bei
  // jeder Zustandsaenderung neu aufgebaut zu werden.
  const eingangsModusRef = useRef(false);
  const schnellCheckinRef = useRef(null);
  const zuletztGescanntRef = useRef({ code: null, zeit: 0 });

  /* ---------------------------------------------------------- Karte laden */

  const karteLaden = useCallback(async (code) => {
    setFehler('');
    try {
      const r = await festAPI.karte(code);
      setKarte(r.data);
      setStatus('');
    } catch (err) {
      setFehler(err?.response?.status === 404
        ? 'Karte nicht gefunden.' : 'Karte konnte nicht geladen werden.');
    }
  }, []);

  // ?c=<code> aus dem QR-Link (Handy-Kamera öffnet die Seite direkt damit)
  useEffect(() => {
    const c = new URLSearchParams(window.location.search).get('c');
    if (c) karteLaden(c);
  }, [karteLaden]);

  /* -------------------------------------------------------------- Kamera */

  const kameraStoppen = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setKameraAn(false);
  }, []);

  // Das <video> haengt erst im DOM, nachdem React neu gerendert hat. Wer den
  // Stream vorher setzt, laeuft auf null und der Nutzer sieht "Kamera nicht
  // verfuegbar", obwohl die Kamera einwandfrei ist.
  const videoAbwarten = useCallback(async () => {
    for (let i = 0; i < 60 && !videoRef.current; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((fertig) => requestAnimationFrame(fertig));
    }
    return videoRef.current;
  }, []);

  const kameraStarten = useCallback(async () => {
    setFehler('');
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
    } catch {
      setFehler('Kamera nicht verfügbar — bitte Losnummer eintippen.');
      setKameraAn(false);
      return;
    }
    streamRef.current = stream;
    setKameraAn(true);

    const video = await videoAbwarten();
    // Zwischenzeitlich abgebrochen (Modus gewechselt, Seite verlassen)?
    if (!video || streamRef.current !== stream) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }

    try {
      video.srcObject = stream;
      await video.play();

      const { default: jsQR } = await import('jsqr');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });

      const tick = () => {
        if (!streamRef.current) return;
        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0);
          const bild = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const qr = jsQR(bild.data, bild.width, bild.height,
                          { inversionAttempts: 'dontInvert' });
          if (qr?.data) {
            const m = qr.data.match(/[?&]c=([A-Za-z0-9_-]+)/);
            const code = m ? m[1] : (qr.data.match(/^[A-Za-z0-9_-]{6,12}$/) ? qr.data : null);
            if (code && eingangsModusRef.current) {
              // Einlass: Kamera bleibt an, dieselbe Karte kurz sperren
              const jetzt = Date.now();
              const zuletzt = zuletztGescanntRef.current;
              if (code !== zuletzt.code || jetzt - zuletzt.zeit > 6000) {
                zuletztGescanntRef.current = { code, zeit: jetzt };
                schnellCheckinRef.current?.(code);
              }
            } else if (code) {
              kameraStoppen();
              karteLaden(code);
              return;
            }
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      // Kamera laeuft, aber der Decoder kam nicht hoch. Eigener Text, sonst
      // sucht am Eingang jemand den Fehler an der falschen Stelle.
      console.error('Scanner-Start fehlgeschlagen:', err);
      setFehler('Scanner konnte nicht starten — Seite neu laden oder Losnummer eintippen.');
      kameraStoppen();
    }
  }, [karteLaden, kameraStoppen, videoAbwarten]);

  useEffect(() => () => kameraStoppen(), [kameraStoppen]);

  /* ------------------------------------------------------------- Check-in */

  // Aufgestautes wegschicken, sobald wieder etwas durchgeht. Am Eingang gibt
  // es keine andere Aktion, die das nebenbei erledigt — also haengt es hier.
  const queueLeeren = useCallback(async () => {
    const q = queueLaden();
    if (!q.length) return;
    const rest = [];
    for (const e of q) {
      try {
        if (e.art === 'checkin') await festAPI.checkin(e.code, e.daten);
        if (e.art === 'founding') await festAPI.foundingSichernTeam(e.code);
        if (e.art === 'challenge') await festAPI.challenge(e.code, e.daten);
        if (e.art === 'gymfights') await festAPI.gymfights(e.code, e.daten);
      } catch { rest.push(e); }
    }
    queueSpeichern(rest); setQueue(rest);
  }, []);

  // Einlass-Scan: eine Anfrage, ein grosser Balken, weiter. Bewusst OHNE
  // Karte nachladen — am Eingang interessiert nur "durch oder nicht".
  const schnellCheckin = useCallback(async (code) => {
    try {
      const r = await festAPI.checkin(code);
      setLetzterCheckin({ ...r.data, um: Date.now() });
      setFehler('');
      queueLeeren();
    } catch (err) {
      if (err?.response?.status === 404) {
        setLetzterCheckin({ unbekannt: true, um: Date.now() });
      } else if (err?.response?.data?.detail) {
        setFehler(err.response.data.detail);
      } else {
        // Kein Netz: merken und beim naechsten Erfolg mitsenden
        const q = [...queueLaden(), { art: 'checkin', code, daten: {} }];
        queueSpeichern(q); setQueue(q);
        setLetzterCheckin({ ausstehend: true, um: Date.now() });
      }
    }
  }, [queueLeeren]);

  useEffect(() => { schnellCheckinRef.current = schnellCheckin; }, [schnellCheckin]);
  useEffect(() => { eingangsModusRef.current = eingangsModus; }, [eingangsModus]);

  // Eingangs-Modus an: Kamera laeuft dauerhaft. Aus: aufraeumen.
  useEffect(() => {
    if (eingangsModus) {
      setKarte(null);
      if (!streamRef.current) kameraStarten();
    } else {
      // Kamera mit ausschalten: das <video> des Einlasses verschwindet, ein
      // weiterlaufender Stream haette danach kein Bild mehr, das er zeigt.
      kameraStoppen();
      setLetzterCheckin(null);
      zuletztGescanntRef.current = { code: null, zeit: 0 };
    }
  }, [eingangsModus, kameraStarten, kameraStoppen]);

  // Offline-Start: der Service-Worker haelt die Seite (Shell + Bundle) vor,
  // damit /fest/scan im Park auch ohne Netz aufgeht; Aktionen puffert die
  // Warteschlange oben. Registrierung scheitert leise (privater Modus).
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/fest/sw.js', { scope: '/fest/' }).catch(() => {});
  }, []);

  /* ----------------------------------------------- Aktionen + Warteschlange */

  const aktion = useCallback(async (beschreibung, ausfuehren, wiederholung = null) => {
    setBusy(true); setFehler('');
    try {
      await ausfuehren();
      setStatus(`${beschreibung} ✓`);
      setTimeout(() => setStatus(''), 2200);
      if (karte) karteLaden(karte.karten_code);
      // Aufgestautes gleich mit versuchen
      await queueLeeren();
    } catch (err) {
      if (err?.response?.data?.detail) {
        setFehler(err.response.data.detail);
      } else if (wiederholung) {
        // Netz weg: Aktion in die Warteschlange, sendet beim nächsten Versuch
        const q = [...queueLaden(), wiederholung];
        queueSpeichern(q); setQueue(q);
        setStatus(`${beschreibung} — ausstehend (kein Netz)`);
      } else {
        setFehler('Aktion fehlgeschlagen — bitte nochmal.');
      }
    }
    setBusy(false);
  }, [karte, karteLaden, queueLeeren]);

  /* ------------------------------------------------------- Unteransichten */

  const [challengeOffen, setChallengeOffen] = useState(null);   // {id, name}
  const [challengeWert, setChallengeWert] = useState('');
  const [challengeKategorie, setChallengeKategorie] = useState('offen');

  const [ptOffen, setPtOffen] = useState(false);
  const [kurse, setKurse] = useState(null);
  const [ptKind, setPtKind] = useState('');

  const [gfAnzahl, setGfAnzahl] = useState(1);
  const [gfBezahlt, setGfBezahlt] = useState(false);
  const [gfOffen, setGfOffen] = useState(false);

  const kurseLaden = async () => {
    setPtOffen(true);
    try {
      const r = await festAPI.kurse((karte?.interesse || []).join(','));
      setKurse(r.data.kurse);
    } catch { setKurse([]); }
  };

  const gemacht = new Set((karte?.challenges || []).map((c) => c.challenge_id));
  const CHALLENGES = [
    ['schlag', 'Härtester Schlag', 'kg'],
    ['airbike', 'Airbike Sprint 30 Sek.', 'kcal'],
    ['ropes', 'Battle Ropes 30 Sek.', 'Wdh.'],
    ['plank', 'Plank-Halten', 'Sek.'],
    ['kettlebell', 'Kettlebell-Swings 60 Sek.', 'Wdh.'],
  ];

  return (
    <div className="fs-root">
      <style>{CSS}</style>
      <header className="fs-kopf">
        <span className="fs-titel">Fest-Scanner</span>
        <button className={eingangsModus ? 'fs-modus fs-modus-an' : 'fs-modus'}
                data-testid="eingangs-modus"
                onClick={() => setEingangsModus((a) => !a)}>
          {eingangsModus ? '● Eingang läuft' : 'Eingangs-Modus'}
        </button>
        <a className="fs-link" href="/fest/dashboard">Dashboard →</a>
      </header>

      {status && <div className="fs-status">{status}</div>}
      {fehler && <div className="fs-fehler">{fehler}</div>}
      {queue.length > 0 && (
        <div className="fs-queue">{queue.length} Aktion(en) ausstehend — werden
          beim nächsten Scan mitgesendet.</div>
      )}

      {eingangsModus && (
        <main className="fs-einlass" data-testid="einlass">
          <video ref={videoRef} className="fs-video" playsInline muted />
          <p className="fs-einlass-hinweis">
            Karte vor die Kamera halten — der Check-in läuft von allein.
          </p>
          {letzterCheckin && <CheckinBalken e={letzterCheckin} />}
          {!letzterCheckin && (
            <div className="fs-balken fs-balken-warte">Bereit. Erster Gast …</div>
          )}
        </main>
      )}

      {!karte && !eingangsModus && (
        <main className="fs-scan">
          {kameraAn ? (
            <>
              <video ref={videoRef} className="fs-video" playsInline muted />
              <button className="fs-cta fs-cta-line" onClick={kameraStoppen}>Kamera stoppen</button>
            </>
          ) : (
            <button className="fs-cta" onClick={kameraStarten}>📷 QR-Code scannen</button>
          )}
          <div className="fs-oder">oder Losnummer eintippen</div>
          <div className="fs-losrow">
            <input inputMode="numeric" placeholder="z. B. 42" value={losEingabe}
                   onChange={(e) => setLosEingabe(e.target.value.replace(/\D/g, ''))}
                   onKeyDown={(e) => e.key === 'Enter' && losEingabe &&
                     festAPI.kartePerLos(losEingabe).then((r) => setKarte(r.data))
                       .catch(() => setFehler('Keine Karte mit dieser Losnummer.'))} />
            <button className="fs-cta" disabled={!losEingabe}
                    onClick={() => festAPI.kartePerLos(losEingabe)
                      .then((r) => { setKarte(r.data); setFehler(''); })
                      .catch(() => setFehler('Keine Karte mit dieser Losnummer.'))}>
              Laden
            </button>
          </div>
        </main>
      )}

      {karte && !eingangsModus && (
        <main className="fs-karte">
          <div className="fs-person">
            <div>
              <span className="fs-los">#{karte.losnummer}</span>
              <h1>{karte.vorname} {karte.nachname}</h1>
              <div className="fs-chips">
                {(karte.interesse || []).map((i) => <span key={i} className="fs-chip">{i.replace('_', ' ')}</span>)}
                {(karte.kinder || []).length > 0 &&
                  <span className="fs-chip">Kinder: {karte.kinder.map((k) => `${k.alter} J.`).join(', ')}</span>}
                {karte.kontakt_erlaubt
                  ? <span className="fs-chip fs-chip-gruen">Kontakt ok</span>
                  : <span className="fs-chip fs-chip-rot">kein Kontakt</span>}
              </div>
            </div>
            <button className="fs-neuscan" onClick={() => { setKarte(null); setLosEingabe(''); }}>
              Nächster Gast
            </button>
          </div>

          {/* A) Check-in — steht oben, weil es der erste Kontakt ist */}
          <section>
            <h2>Check-in</h2>
            {karte.checkin ? (
              <div className="fs-checkin-da">
                <span>✓ Eingecheckt um {uhrzeit(karte.checkin.zeit)}
                  {karte.checkin.mitarbeiter_name ? ` · ${karte.checkin.mitarbeiter_name}` : ''}</span>
                <button className="fs-cta fs-cta-line" disabled={busy}
                        data-testid="checkin-zurueck"
                        onClick={() => aktion('Check-in zurückgenommen',
                          () => festAPI.checkin(karte.karten_code, { rueckgaengig: true }))}>
                  Fehlscan? Zurücknehmen
                </button>
              </div>
            ) : (
              <button className="fs-cta fs-cta-gross" disabled={busy}
                      data-testid="checkin-knopf"
                      onClick={() => aktion('Eingecheckt',
                        () => festAPI.checkin(karte.karten_code),
                        { art: 'checkin', code: karte.karten_code, daten: {} })}>
                ✓ Gast ist da
              </button>
            )}
          </section>

          {/* A2) Founding "Small Group" — nur waehrend das Angebot laeuft.
                 Dieselben zwoelf Plaetze wie auf der Gastkarte; wer hier
                 tippt, steht als Erfasser drin. */}
          {karte.founding_stand?.offen && (
            <section>
              <h2>Small Group Founding</h2>
              {karte.founding?.status ? (
                <div className="fs-checkin-da" data-testid="founding-da">
                  <span>{karte.founding.status === 'platz'
                    ? `✓ Platz gesichert${karte.founding.erfasst_von ? ` · ${karte.founding.erfasst_von}` : ''}`
                    : '✓ Auf der Warteliste'}</span>
                </div>
              ) : (
                <>
                  <p className="fs-founding-frei" data-testid="founding-frei">
                    {karte.founding_stand.warteliste_aktiv
                      ? `Alle ${karte.founding_stand.gesamt} Plätze vergeben`
                      : <>Noch <b>{karte.founding_stand.frei}</b> von {karte.founding_stand.gesamt} Plätzen</>}
                  </p>
                  <button className="fs-cta fs-cta-gross" disabled={busy}
                          data-testid="founding-knopf"
                          onClick={() => aktion(
                            karte.founding_stand.warteliste_aktiv ? 'Auf die Warteliste gesetzt' : 'Platz gesichert',
                            () => festAPI.foundingSichernTeam(karte.karten_code),
                            { art: 'founding', code: karte.karten_code, daten: {} })}>
                    {karte.founding_stand.warteliste_aktiv ? 'Warteliste' : 'Small Group Founding'}
                  </button>
                </>
              )}
            </section>
          )}

          {/* B) Challenges */}
          <section>
            <h2>Challenge abhaken</h2>
            <div className="fs-aktionen">
              {CHALLENGES.map(([id, name]) => (
                <button key={id}
                        className={gemacht.has(id) ? 'fs-aktion fs-aktion-fertig' : 'fs-aktion'}
                        onClick={() => {
                          const alt = (karte.challenges || []).find((c) => c.challenge_id === id);
                          setChallengeOffen({ id, name });
                          setChallengeWert(alt?.wert || '');
                          setChallengeKategorie(alt?.kategorie || 'offen');
                        }}>
                  {gemacht.has(id) ? '✓ ' : ''}{name}
                </button>
              ))}
            </div>
            <p className="fs-mini">
              {gemacht.size} Challenge(s) — ab 3 ist die Karte im Lostopf.
              Fertige lassen sich antippen und korrigieren.
            </p>
          </section>

          {/* B) Probetraining */}
          <section>
            <h2>Probetraining buchen</h2>
            {karte.probetraining_anzeige ? (
              <p className="fs-gebucht">✓ {karte.probetraining_anzeige.kurs} —{' '}
                {karte.probetraining_anzeige.datum} {karte.probetraining_anzeige.uhrzeit} Uhr</p>
            ) : ptOffen ? (
              <div className="fs-kurse">
                {(karte.interesse || []).includes('kinder') && (
                  <input className="fs-kindfeld" placeholder="Vorname des Kindes (wenn fürs Kind)"
                         value={ptKind} onChange={(e) => setPtKind(e.target.value)} />
                )}
                {kurse === null && <p className="fs-mini">Kurstermine werden geladen …</p>}
                {kurse?.length === 0 && (
                  <p className="fs-mini">Keine buchbaren Kurstermine gefunden —
                    bitte im CRM prüfen, ob der Terminplan generiert ist.</p>
                )}
                {kurse?.map((k) => (
                  <button key={k.termin_id} className="fs-kurs" disabled={busy}
                          onClick={() => aktion(`Probetraining ${k.kurs}`,
                            () => festAPI.probetraining(karte.karten_code,
                              { termin_id: k.termin_id, kind_vorname: ptKind.trim() }))}>
                    <b>{k.kurs}</b>
                    <span>{k.wochentag} {k.start.slice(8, 10)}.{k.start.slice(5, 7)}. · {k.start.slice(11)} Uhr · {k.frei} frei</span>
                  </button>
                ))}
              </div>
            ) : (
              <button className="fs-cta fs-cta-line" onClick={kurseLaden}>
                Kurs & Termin auswählen
              </button>
            )}
          </section>

          {/* C) Gymfights */}
          <section>
            <h2>Gymfights-Ticket 26.09.</h2>
            {karte.gymfights ? (
              <p className="fs-gebucht">✓ {karte.gymfights.anzahl} Ticket(s)
                {karte.gymfights.bezahlt ? ' — bezahlt' : ' — reserviert'}</p>
            ) : gfOffen ? (
              <div className="fs-gf">
                <label>Anzahl
                  <select value={gfAnzahl} onChange={(e) => setGfAnzahl(Number(e.target.value))}>
                    {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <label className="fs-gf-haken">
                  <input type="checkbox" checked={gfBezahlt}
                         onChange={(e) => setGfBezahlt(e.target.checked)} /> bezahlt
                </label>
                <button className="fs-cta" disabled={busy}
                        onClick={() => aktion('Gymfights-Ticket',
                          () => festAPI.gymfights(karte.karten_code,
                            { anzahl: gfAnzahl, bezahlt: gfBezahlt }),
                          { art: 'gymfights', code: karte.karten_code,
                            daten: { anzahl: gfAnzahl, bezahlt: gfBezahlt } })}>
                  Speichern
                </button>
              </div>
            ) : (
              <button className="fs-cta fs-cta-line" onClick={() => setGfOffen(true)}>
                Ticket vermerken
              </button>
            )}
          </section>
        </main>
      )}

      {/* Challenge-Wert-Dialog */}
      {challengeOffen && (
        <div className="fs-overlay" onClick={() => setChallengeOffen(null)}>
          <div className="fs-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>{challengeOffen.name}</h2>
            <input autoFocus placeholder="Wert (optional, z. B. 187)"
                   inputMode="decimal" value={challengeWert}
                   onChange={(e) => setChallengeWert(e.target.value)} />
            <div className="fs-katrow">
              {['offen', 'kinder', 'frauen', 'maenner'].map((k) => (
                <button key={k}
                        className={challengeKategorie === k ? 'fs-chip fs-chip-an' : 'fs-chip'}
                        onClick={() => setChallengeKategorie(k)}>{k}</button>
              ))}
            </div>
            <button className="fs-cta" disabled={busy}
                    onClick={() => {
                      const daten = { challenge_id: challengeOffen.id,
                                      wert: challengeWert.trim(),
                                      kategorie: challengeKategorie };
                      setChallengeOffen(null);
                      aktion(challengeOffen.name,
                        () => festAPI.challenge(karte.karten_code, daten),
                        { art: 'challenge', code: karte.karten_code, daten });
                    }}>
              Abhaken & speichern
            </button>
            <button className="fs-cta fs-cta-line" onClick={() => setChallengeOffen(null)}>
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const CSS = `
.fs-root { min-height: 100vh; background: #0E0E10; color: #F5F5F4;
  font-family: Inter, system-ui, sans-serif; font-size: 16px; }
.fs-root * { box-sizing: border-box; }
.fs-root h1 { font-family: Anton, sans-serif; font-weight: 400; font-size: 26px;
  letter-spacing: .04em; margin: 2px 0 8px; text-transform: uppercase; }
.fs-root h2 { font-size: 15px; letter-spacing: .12em; text-transform: uppercase;
  color: #f5821f; margin: 22px 0 10px; }
.fs-kopf { display: flex; justify-content: space-between; align-items: center;
  padding: 14px 18px; border-bottom: 1px solid rgba(255,255,255,.1); }
.fs-titel { font-family: Anton, sans-serif; letter-spacing: .06em; }
.fs-link { color: #F5F5F4; opacity: .7; text-decoration: none; font-size: 14px; }
.fs-status { background: rgba(74,222,128,.15); color: #4ADE80; padding: 12px 18px;
  font-weight: 600; }
.fs-fehler { background: rgba(245,130,31,.14); color: #FF8A80; padding: 12px 18px; }
.fs-queue { background: rgba(250,204,21,.12); color: #FACC15; padding: 10px 18px; font-size: 14px; }
.fs-scan, .fs-karte, .fs-einlass { padding: 20px 18px 60px; max-width: 560px; margin: 0 auto; }
/* Eingangs-Modus: der Balken muss aus zwei Metern lesbar sein, waehrend
   der Mitarbeiter das Handy in der Hand haelt und der Naechste schon steht. */
.fs-modus { font: inherit; font-size: 13px; font-weight: 700; color: inherit;
  background: #1A1A1D; border: 1px solid rgba(255,255,255,.22); border-radius: 999px;
  padding: 8px 14px; cursor: pointer; }
.fs-modus-an { background: #4ADE80; color: #0E0E10; border-color: #4ADE80; }
.fs-einlass-hinweis { text-align: center; opacity: .6; font-size: 14px; margin: 4px 0 14px; }
.fs-balken { border-radius: 16px; padding: 22px 20px; display: flex; flex-direction: column;
  gap: 6px; text-align: center; }
.fs-balken b { font-family: Anton, sans-serif; font-weight: 400; font-size: 30px;
  letter-spacing: .03em; line-height: 1.1; }
.fs-balken span { font-size: 16px; opacity: .85; }
.fs-balken-gruen { background: #14532D; color: #DCFCE7; }
.fs-balken-gelb  { background: #4A3B0A; color: #FEF3C7; }
.fs-balken-rot   { background: #4C1213; color: #FEE2E2; }
.fs-balken-warte { background: #1A1A1D; color: rgba(245,245,244,.55); padding: 18px;
  text-align: center; }
.fs-cta-gross { font-size: 19px; padding: 20px 22px; }
.fs-founding-frei { margin: 0 0 12px; font-size: 15px; opacity: .85; }
.fs-founding-frei b { font-family: Anton, sans-serif; font-weight: 400;
  font-size: 32px; line-height: 1; color: #f5821f; vertical-align: -3px; margin: 0 3px; }
.fs-checkin-da { display: flex; flex-direction: column; gap: 8px;
  background: rgba(74,222,128,.12); color: #4ADE80; border-radius: 14px; padding: 14px 16px; }
.fs-video { width: 100%; border-radius: 16px; margin-bottom: 12px; }
.fs-oder { text-align: center; opacity: .55; margin: 20px 0 10px; font-size: 14px; }
.fs-losrow { display: flex; gap: 10px; }
.fs-losrow input { flex: 1; font: inherit; font-size: 20px; color: inherit;
  background: #1A1A1D; border: 1px solid rgba(255,255,255,.16); border-radius: 12px;
  padding: 12px 14px; min-width: 0; }
.fs-losrow .fs-cta { width: auto; margin: 0; }
.fs-cta { display: block; width: 100%; font: inherit; font-weight: 700; font-size: 16.5px;
  color: #fff; background: #f5821f; border: none; border-radius: 999px;
  padding: 15px 22px; cursor: pointer; margin-top: 8px; }
.fs-cta:disabled { opacity: .55; }
.fs-cta-line { background: transparent; border: 1px solid rgba(255,255,255,.25); font-weight: 500; }
.fs-person { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
.fs-los { font-family: Anton, sans-serif; font-size: 20px; color: #f5821f; }
.fs-neuscan { font: inherit; font-size: 13.5px; color: inherit; background: #1A1A1D;
  border: 1px solid rgba(255,255,255,.2); border-radius: 999px; padding: 10px 14px;
  cursor: pointer; flex: none; }
.fs-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.fs-chip { font-size: 12.5px; background: #1A1A1D; border: 1px solid rgba(255,255,255,.18);
  border-radius: 999px; padding: 5px 11px; color: inherit; cursor: pointer; font-family: inherit; }
.fs-chip-an { background: #f5821f; border-color: #f5821f; }
.fs-chip-gruen { color: #4ADE80; border-color: rgba(74,222,128,.5); }
.fs-chip-rot { color: #FF8A80; border-color: rgba(245,130,31,.5); }
.fs-aktionen { display: grid; gap: 10px; }
.fs-aktion { font: inherit; font-size: 16px; text-align: left; color: inherit;
  background: #1A1A1D; border: 1px solid rgba(255,255,255,.14); border-radius: 14px;
  padding: 16px; min-height: 56px; cursor: pointer; }
.fs-aktion-fertig { background: rgba(74,222,128,.1); border-color: rgba(74,222,128,.55);
  color: #4ADE80; }
.fs-mini { font-size: 13px; opacity: .6; margin: 8px 0 0; }
.fs-gebucht { color: #4ADE80; font-weight: 600; }
.fs-kurse { display: grid; gap: 10px; }
.fs-kindfeld { font: inherit; color: inherit; background: #1A1A1D;
  border: 1px solid rgba(255,255,255,.16); border-radius: 12px; padding: 12px 14px; }
.fs-kurs { font: inherit; text-align: left; color: inherit; background: #1A1A1D;
  border: 1px solid rgba(255,255,255,.14); border-radius: 14px; padding: 13px 15px;
  cursor: pointer; display: flex; flex-direction: column; gap: 3px; }
.fs-kurs span { font-size: 13.5px; opacity: .65; }
.fs-gf { display: grid; gap: 12px; }
.fs-gf label { display: flex; align-items: center; gap: 10px; }
.fs-gf select { font: inherit; color: inherit; background: #1A1A1D;
  border: 1px solid rgba(255,255,255,.16); border-radius: 10px; padding: 10px; }
.fs-gf-haken input { width: 20px; height: 20px; accent-color: #f5821f; }
.fs-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.7);
  display: flex; align-items: flex-end; justify-content: center; z-index: 40; }
.fs-dialog { background: #17171A; border-radius: 20px 20px 0 0; padding: 22px 18px 30px;
  width: 100%; max-width: 560px; display: grid; gap: 12px; }
.fs-dialog h2 { margin: 0; }
.fs-dialog input { font: inherit; font-size: 22px; color: inherit; background: #0E0E10;
  border: 1px solid rgba(255,255,255,.2); border-radius: 12px; padding: 14px; }
.fs-katrow { display: flex; gap: 8px; flex-wrap: wrap; }
`;
